<!-- overview page -->
<script setup lang="ts">
import { useStore } from '@/stores/main';
import PlanModal from '@/views/Overview/PlanModal.vue';
import SettingsModal from '@/views/SettingsModal.vue';
import {
  alertController,
  IonContent,
  IonIcon,
  IonLoading,
  IonPage,
  loadingController,
  modalController,
} from '@ionic/vue';
import { useIonRouter } from '@ionic/vue';
import { computed, ref, onMounted } from 'vue';
import SpeechBubble from '@/components/SpeechBubble.vue';
import dayjs from 'dayjs';
import 'dayjs/locale/sv';
import 'dayjs/locale/en';
import { useI18n } from 'vue-i18n';

// Icons and Images
import logoUrl from '/assets/logo.png';
import monsterUrl from '/assets/monster/vinka.png';
import {
  arrowDownCircleOutline,
  informationCircleOutline,
  menuOutline,
  timeOutline,
  addCircleOutline,
  checkmarkCircleOutline,
  chevronForwardOutline,
} from 'ionicons/icons';

const router = useIonRouter();
const store = useStore();
const { t, locale } = useI18n();

const hasActiveProfile = computed(() => store.profile !== null);
const hasCompletedProfiles = computed(() => store.profiles.length > 0);
const showAllProfiles = ref(false);
const isLoaded = ref(false);

// Logout loading state
const isLoggingOut = ref(false);

// Computed property to get the current locale for dayjs
const dayjsLocale = computed(() => {
  return locale.value === 'en_us' ? 'en' : 'sv';
});

onMounted(() => {
  setTimeout(() => {
    isLoaded.value = true;
  }, 300);
});

async function openSettingsModal() {
  const modal = await modalController.create({
    component: SettingsModal,
  });
  await modal.present();
}

const fetchingNewPlan = ref(false);

async function newPlan() {
  fetchingNewPlan.value = true;
  await store.getNewPlan();
  fetchingNewPlan.value = false;
  setTimeout(() => router.replace('/evaluate'), 500);
}

async function showProfile(profileId: number) {
  const profile = store.profiles.filter(p => p.id === profileId)[0];
  const loading = await loadingController.create({
    message: t('home.loadingPlan'),
    cssClass: 'custom-loading'
  });
  await loading.present();
  const plan = await store.fetchPlanById(profileId);
  await loading.dismiss();

  if (!plan.goals) {
    const alert = await alertController.create({
      header: t('home.errorHeader'),
      message: t('home.errorMessage'),
      buttons: [t('home.okay')],
      cssClass: 'custom-alert'
    });
    await alert.present();
    return;
  }

  const modal = await modalController.create({
    component: PlanModal,
    componentProps: { profile, plan },
    cssClass: 'plan-modal'
  });
  await modal.present();
}

async function handleLogout() {
  try {
    isLoggingOut.value = true;
    await store.logout();
    router.replace('/login');
  } catch (error) {
    console.error('Logout failed:', error);
    // Optionally show an error alert here
  } finally {
    isLoggingOut.value = false;
  }
}

function formatDate(date: string) {
  return dayjs(date).locale(dayjsLocale.value).format('D MMMM, YYYY');
}
</script>

<template>
  <ion-page>
    <ion-content :fullscreen="true" :scroll-y="true">
      <ion-loading :is-open="fetchingNewPlan" :message="$t('home.loading')" cssClass="custom-loading" />
      <ion-loading :is-open="isLoggingOut" message="Loggar ut..." cssClass="custom-loading" />

      <div class="flex flex-col h-auto px-4 md:px-6 pt-0">
        <!-- Header Section -->
        <header class="header-container">
          <!-- Left: Menu Button -->
          <button
            class="menu-button"
            @click="openSettingsModal"
          >
            <ion-icon :icon="menuOutline" class="menu-icon"></ion-icon>
          </button>

          <!-- Center: Logo -->
          <div class="logo-container">
            <img :src="logoUrl" class="header-logo" />
          </div>

          <!-- Right: Logout Button -->
          <button
            class="logout-btn"
            :disabled="isLoggingOut"
            @click="handleLogout"
          >
            {{ isLoggingOut ? '...' : t('navigation.logout') }}
          </button>
        </header>

        <!-- Main Content - Preserving the original welcome section structure -->
        <main class="mt-4 text-center w-full max-w-lg mx-auto content-container" :class="{'fade-in': isLoaded}">
          <!-- Welcome Section - Exactly as in original -->
          <div class="flex flex-col items-center overflow-y-auto mb-8">
            <speech-bubble
              color="purple"
              class="w-[70vw] md:max-w-[32rem] md:landscape:max-w-[18rem] lg:max-w-[24rem] pt-[20px] min-h-[5rem]"
            >
              <div class="flex flex-col justify-center h-full pt-6 md:pt-4 px-4 text-center">
                <p class="text-base md:text-lg font-medium leading-relaxed">
                  {{
                    hasActiveProfile || hasCompletedProfiles
                      ? $t('home.welcomeBack')
                      : $t('home.welcome')
                  }}
                </p>
                <p class="text-sm md:text-base mt-2 leading-relaxed">
                  {{
                    hasCompletedProfiles
                      ? $t('home.pastPlansInfo')
                      : $t('home.getStartedPrompt')
                  }}
                </p>
              </div>
            </speech-bubble>
            <img class="!w-[70%] md:!w-[40%] lg:!w-[35%] !max-w-[250px] mt-4 monster-image" :src="monsterUrl" />
          </div>

          <!-- Active Plan Notice - Enhanced styling while preserving structure -->
          <div
            v-if="hasActiveProfile"
            class="mb-8 active-plan-card"
            :class="{'slide-up': isLoaded}"
          >
            <div class="flex items-center gap-4 p-4 md:p-6">
              <div class="card-icon-container">
                <ion-icon :icon="timeOutline" class="card-icon"></ion-icon>
              </div>
              <div class="flex-1">
                <p>{{ $t('home.reminderActivePlan') }}</p>
              </div>
            </div>
            <b-button
              @click="router.push('/evaluate')"
              class="!bg-green-darker !hover:bg-green-dark w-full !rounded-t-none flex justify-center items-center gap-2"
            >
              <span>{{ $t('home.continue') }}</span>
              <ion-icon :icon="chevronForwardOutline"></ion-icon>
            </b-button>
          </div>

          <!-- Completed Plans Section - Enhanced styling -->
          <div v-if="hasCompletedProfiles" class="mb-8 completed-plans" :class="{'slide-up': isLoaded}" style="--delay: 0.2s">
            <ul
              class="list-none p-0 w-full text-left border-2 border-purple-dark rounded-xl overflow-hidden enhanced-list"
            >
              <!-- Latest Plan - Enhanced with same structure -->
              <li>
                <a
                  href="#"
                  class="p-3 flex justify-between items-center text-white bg-purple-dark hover:bg-purple-darker latest-plan"
                  @click.prevent="showProfile(store.profiles[0].id)"
                >
                  <div>
                    <div class="text-lg">{{ $t('home.latestPlan') }}</div>
                    <div class="text-sm font-bold">
                      {{ formatDate(store.profiles[0].created_at) }}
                    </div>
                  </div>
                  <ion-icon :icon="informationCircleOutline" class="text-2xl plan-icon" />
                </a>
              </li>
              
              <!-- Toggle Previous Plans - Enhanced with same structure -->
              <li v-if="hasCompletedProfiles && store.profiles.length > 1">
                <a
                  href="#"
                  class="p-3 flex justify-between items-center text-purple-darker bg-black bg-opacity-5 hover:bg-opacity-10 toggle-plans"
                  @click.prevent="() => (showAllProfiles = !showAllProfiles)"
                >
                  <div>
                    {{
                      showAllProfiles
                        ? $t('home.hideOlderPlans')
                        : $t('home.showAllPlans')
                    }}
                  </div>
                  <ion-icon :icon="arrowDownCircleOutline" class="text-2xl" :class="{'rotated': showAllProfiles}" />
                </a>
              </li>
              
              <!-- Previous Plans List - Enhanced with same structure -->
              <template v-if="showAllProfiles">
                <li
                  v-for="(profile, index) in store.profiles.slice(1)"
                  :key="index"
                  class="p-3 bg-black bg-opacity-5 hover:bg-opacity-10 previous-plan"
                >
                  <a
                    href="#"
                    class="flex justify-between items-center text-purple-darker"
                    @click.prevent="showProfile(profile.id)"
                  >
                    <div class="text-sm font-bold">
                      {{ formatDate(profile.created_at) }}
                    </div>
                    <ion-icon
                      :icon="informationCircleOutline"
                      class="text-2xl plan-icon"
                    />
                  </a>
                </li>
              </template>
            </ul>
          </div>

          <!-- New Plan Section - Enhanced styling while preserving structure -->
          <div
            v-if="!hasActiveProfile"
            class="mb-8 new-plan-card"
            :class="{'slide-up': isLoaded}"
            style="--delay: 0.3s"
          >
            <div class="flex items-center gap-4 p-4 md:p-6">
              <div class="card-icon-container">
                <ion-icon :icon="addCircleOutline" class="card-icon"></ion-icon>
              </div>
              <div class="flex-1">
                <p class="leading-relaxed">
                  {{
                    hasCompletedProfiles
                      ? $t('home.newPlanPrompt')
                      : $t('home.startFirstPlanPrompt')
                  }}
                </p>
              </div>
            </div>
            <b-button 
              @click="newPlan" 
              class="bg-blue-darker w-full !rounded-t-none flex justify-center items-center gap-2"
            >
              <span>{{ hasCompletedProfiles ? $t('home.createNewPlan') : $t('home.getStarted') }}</span>
              <ion-icon :icon="chevronForwardOutline"></ion-icon>
            </b-button>
          </div>
        </main>
      </div>

      <!-- Decorative Elements -->
      <div class="decorative-shape shape-1"></div>
      <div class="decorative-shape shape-2"></div>
    </ion-content>
  </ion-page>
</template>

<style scoped>
/* Base Styles */
ion-content {
  --background: theme('colors.primary.background');
  --padding-start: 1rem;
  --padding-end: 1rem;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* Header Container */
.header-container {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  height: 4.5rem;
  padding: 0;
}

/* Menu Button - Left */
.menu-button {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 2rem;
  height: 2rem;
  background: transparent;
  border: none;
  color: #7b1fa2;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-left: 0;
}

.menu-button:hover {
  color: #4a148c;
  transform: scale(1.1);
}

.menu-icon {
  font-size: 1.5rem;
}

/* Logo Container - Center */
.logo-container {
  display: flex;
  justify-content: center;
}

.header-logo {
  width: 6rem;
  height: auto;
  object-fit: contain;
  transition: transform 0.3s ease;
}

.header-logo:hover {
  transform: scale(1.05);
}

/* Logout Button - Right */
.logout-btn {
  justify-self: end;
  padding: 0.35rem 0.6rem;
  background-color: #7b1fa2;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.65rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  margin-right: 0;
}

.logout-btn:hover {
  background-color: #6a1b9a;
  transform: scale(1.02);
}

.logout-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Content Container Animation */
.content-container {
  opacity: 0;
  transform: translateY(10px);
  transition: opacity 0.5s ease, transform 0.5s ease;
}

.content-container.fade-in {
  opacity: 1;
  transform: translateY(0);
}

/* Monster Image Animation */
.monster-image {
  transition: transform 0.5s ease;
  animation: float 4s ease-in-out infinite;
}

@keyframes float {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

/* Card Enhancements */
.active-plan-card, .new-plan-card {
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  opacity: 0;
  transform: translateY(20px);
}

.active-plan-card {
  background-color: #e8f5e9;
}

.new-plan-card {
  background-color: #e3f2fd;
}

.active-plan-card:hover, .new-plan-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
}

.active-plan-card.slide-up, .new-plan-card.slide-up, .completed-plans.slide-up {
  animation: slideUp 0.6s ease forwards;
  animation-delay: var(--delay, 0s);
}

@keyframes slideUp {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Icon Container Styles */
.card-icon-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  border-radius: 12px;
  flex-shrink: 0;
}

.active-plan-card .card-icon-container {
  background-color: rgba(76, 175, 80, 0.2);
  color: #2e7d32;
}

.new-plan-card .card-icon-container {
  background-color: rgba(33, 150, 243, 0.2);
  color: #1976d2;
}

.card-icon {
  font-size: 1.5rem;
}

/* Enhanced List Styling */
.enhanced-list {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.enhanced-list:hover {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
}

.latest-plan {
  transition: background-color 0.3s ease;
}

.plan-icon {
  transition: transform 0.3s ease;
}

.previous-plan:hover .plan-icon, .latest-plan:hover .plan-icon {
  transform: scale(1.2);
}

/* Toggle Plans Animation */
.toggle-plans ion-icon {
  transition: transform 0.3s ease;
}

.toggle-plans ion-icon.rotated {
  transform: rotate(180deg);
}

/* Decorative Shapes */
.decorative-shape {
  position: absolute;
  border-radius: 50%;
  z-index: 0;
  opacity: 0.3;
  filter: blur(50px);
  pointer-events: none;
}

.shape-1 {
  width: 300px;
  height: 300px;
  background: radial-gradient(circle, rgba(123, 31, 162, 0.1), rgba(123, 31, 162, 0.05));
  top: -100px;
  right: -100px;
}

.shape-2 {
  width: 250px;
  height: 250px;
  background: radial-gradient(circle, rgba(33, 150, 243, 0.1), rgba(33, 150, 243, 0.05));
  bottom: -50px;
  left: -100px;
}

/* Loading and Alert Custom Styles */
:global(.custom-loading) {
  --background: rgba(255, 255, 255, 0.9);
  --spinner-color: #7b1fa2;
  --border-radius: 12px;
  --box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

:global(.custom-alert) {
  --background: white;
  --border-radius: 12px;
  --box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

:global(.plan-modal) {
  --border-radius: 16px;
}
</style>